import { ConstrainMode, IColumn, IDetailsRowProps, IObjectWithKey, mergeStyleSets } from '@fluentui/react';
import { IPFontIcon, IPScrollablePane, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode, IPSelection } from 'baseComponents/Utils/Selection';
import { globalStackTokensGapSmall, listTypeSitePageLibraryIcon } from 'globalStyles';
import ResourceLink from 'models/resourceLink';
import { useTranslation } from 'react-i18next';
import { toLocaleDateNumeric } from 'utils/datetime';
import { ISitePage } from '../../services/Graph/SharepointInterfaces';

interface IMySitePagesProps {
  pages: ISitePage[];
  onSelect?: (items: ISitePage[]) => void;
  isListLoading: boolean;
  existingLinks: ResourceLink[];
  linkToEdit?: ResourceLink;
  singleSelect?: boolean;
}

const MySitePages = (props: IMySitePagesProps) => {
  const { t } = useTranslation(['sharepoint']);

  const columns: IColumn[] = [
    {
      key: 'icon',
      minWidth: 15,
      maxWidth: 15,
      onRender: () => {
        return (
          <IPStack verticalAlign="center">
            <IPFontIcon {...listTypeSitePageLibraryIcon} className={classes.icon} />
          </IPStack>
        );
      },
      name: '',
    },
    {
      key: 'page',
      minWidth: 200,
      name: t('sharepoint:SitePagePicker.Columns.Page'),
      onRender: (item?: ISitePage, index?: number | undefined, column?: IColumn | undefined) => {
        if (!item) return;

        return <IPText>{item.title || item.name}</IPText>;
      },
    },
    {
      key: 'changed',
      minWidth: 90,
      isResizable: true,
      name: t('sharepoint:SitePagePicker.Columns.Changed'),
      onRender: (item?: ISitePage, index?: number | undefined, column?: IColumn | undefined) => {
        if (!item || item.lastModifiedDateTime === undefined) return;

        return <IPText>{toLocaleDateNumeric(new Date(item.lastModifiedDateTime))}</IPText>;
      },
    },
    {
      key: 'changedBy',
      minWidth: 110,
      isResizable: true,
      name: t('sharepoint:SitePagePicker.Columns.ChangedBy'),
      onRender: (item?: ISitePage, index?: number | undefined, column?: IColumn | undefined) => {
        if (!item) return;

        return <IPText>{item.lastModifiedBy?.user?.displayName}</IPText>;
      },
    },
  ];

  const classes = mergeStyleSets({
    icon: {
      fontSize: '16px',
    },
  });

  if (props.isListLoading) {
    return (
      <IPStack verticalFill verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const selection = new IPSelection<ISitePage>({
    canSelectItem(item) {
      return (
        item.id !== undefined &&
        !props.existingLinks?.find(
          (existingLink) =>
            props.linkToEdit?.linkId !== existingLink.linkId &&
            (existingLink.pageId === item.id || existingLink.linkName.toLowerCase() === item.name?.toLowerCase()),
        )
      );
    },
    getKey(item) {
      return item.id || '';
    },
    onSelectionChanged: () => {
      const item = selection.getSelection() as ISitePage[];
      if (props.onSelect) props.onSelect(item);
    },
  });

  if (props.pages.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('sharepoint:SharepointPicker.NoData')}</IPText>
      </IPStack>
    );
  }

  const onRenderRow = (
    rowProps?: IDetailsRowProps,
    defaultRender?: (rowProps?: IDetailsRowProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (defaultRender && rowProps?.item && !selection.canSelectItem(rowProps.item) && !rowProps.item.folder) {
      rowProps.disabled = true;

      return (
        <IPTooltipHost
          content={t('sharepoint:SharepointPicker.ItemExists')}
          directionalHint={IPDirectionalHint.topLeftEdge}
        >
          {defaultRender(rowProps)}
        </IPTooltipHost>
      );
    }

    if (defaultRender) {
      return defaultRender(rowProps);
    } else {
      return null;
    }
  };

  //
  // Main render
  //
  return (
    <IPStack grow styles={{ root: { position: 'relative' } }} verticalFill>
      <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
        <IPStack.Item tokens={globalStackTokensGapSmall} grow verticalFill>
          <IPDetailsList
            selection={selection as IPSelection<IObjectWithKey>}
            columns={columns}
            items={props.pages}
            layoutMode={IPDetailsListLayoutMode.justified}
            constrainMode={ConstrainMode.unconstrained}
            isHeaderVisible={true}
            selectionMode={
              props.onSelect
                ? props.singleSelect
                  ? IPSelectionMode.single
                  : IPSelectionMode.multiple
                : IPSelectionMode.none
            }
            onRenderRow={onRenderRow}
          />
        </IPStack.Item>
      </IPScrollablePane>
    </IPStack>
  );
};

export default MySitePages;
